<script setup lang="ts">
import { computed } from 'vue';
import { Pie } from 'vue-chartjs';
import { useMobile } from '@/composables/useMobile';
import { careerColors } from '@/utils/careers';
import type { CareerCount } from '@/api/monthly-summaries';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Title, Tooltip, Legend);

interface Props {
  careerData: CareerCount[];
}

const props = defineProps<Props>();

const { isPhone } = useMobile();

const CAREER_COLOR_MAP: Record<string, string> = {
  'green': '#4CAF50',
  'blue': '#2196F3',
  'red-darken-2': '#C62828',
  'orange-darken-2': '#EF6C00',
  'yellow-darken-3': '#F57F17',
  'cyan': '#00BCD4',
  'pink': '#E91E63',
  'brown-darken-2': '#5D4037',
  'green-darken-4': '#1B5E20',
  'purple': '#9C27B0',
  'indigo': '#3F51B5',
  'lime-darken-2': '#689F38',
  'teal': '#009688',
  'deep-purple': '#673AB7',
  'red-darken-4': '#B71C1C',
};

const chartData = computed(() => {
  const sortedData = [...props.careerData].sort((a, b) => a.career.localeCompare(b.career));

  const labels = sortedData.map(item => item.label);
  const data = sortedData.map(item => item.count);
  const colors = sortedData.map(item => {
    const colorName = careerColors[item.career as keyof typeof careerColors];

    return CAREER_COLOR_MAP[colorName] || '#9C27B0';
  });

  return {
    labels,
    datasets: [
      {
        label: 'Turnos Mensuales',
        data,
        backgroundColor: colors,
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };
});

// On phones the right-side legend overlaps the pie; move it below and give
// the chart a fixed height so the two stack cleanly. Desktop is unchanged.
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: isPhone.value ? ('bottom' as const) : ('right' as const),
    },
  },
}));
</script>

<template>
  <div :style="isPhone ? { height: '260px' } : {}">
    <Pie
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>
